import { Control, ControlCodes, getControlName } from "@theprelude/core";
import { InfoIcon, ToggleGroup, ToggleItem } from "@theprelude/ds";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import { ConnectWithEntraIdButton } from "../connect-entra-id-button";
import { IntegrationForm } from "../integration-form";
import { IntegrationOverlayView } from "../integration-overlay";
import { OrBorder } from "../or-border";
import { PartnerGroupTable } from "../partner-group-table";

const controlCode = ControlCodes.ENTRA;
const controlName = getControlName(controlCode);

export const EntraIntegrationOverlay = ({
  instance,
  view,
}: {
  instance?: Control;
  view: IntegrationOverlayView;
}) => {
  const overlay = useOverlay();

  return (
    <div className="h-full">
      <div className="pt-4 pl-4">
        <ToggleGroup
          value={view}
          onValueChange={(value) => {
            void overlay.open("integration", {
              instanceId: instance?.instance_id,
              control: controlCode,
              view: value,
            });
          }}
        >
          <ToggleItem value="form">Settings</ToggleItem>
          <ToggleItem value="groups" disabled={!instance}>
            Groups
          </ToggleItem>
        </ToggleGroup>
      </div>
      {view === "groups" && instance ? (
        <div className="flex h-full flex-col gap-4 p-4">
          <p className="m-0 text-primary-foreground leading-6">
            Choose which Microsoft Entra ID groups to sync with Control Monitor.
            Microsoft Entra ID (formerly Azure Active Directory) is a
            cloud-based identity and access management service that helps manage
            user identities and control access to your apps.
          </p>
          <a
            href="https://support.preludesecurity.com/entra-id-group-filtering"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1"
          >
            <InfoIcon className="text-secondary-foreground" />
            <span className="underline underline-offset-2">
              How are Microsoft Entra ID Active Directory Groups used?
            </span>
          </a>

          <PartnerGroupTable instance={instance} />
        </div>
      ) : (
        <EntraIntegrationForm instance={instance} />
      )}
    </div>
  );
};

const EntraIntegrationForm = ({ instance }: { instance?: Control }) => (
  <IntegrationForm
    control={controlCode}
    instanceId={instance?.instance_id}
    instanceName={instance?.name ?? undefined}
    description={`Connect to ${controlName} and get a full view of your Microsoft-managed identities.  When connecting, an app registration will
        be created, requiring a set of permissions for successful setup.`}
    extraOptions={
      <>
        <ConnectWithEntraIdButton
          controls={[
            {
              controlCode,
              instanceId: instance?.instance_id ?? "",
              instanceName: instance?.name ?? "",
              baseUrl:
                instance?.api ?? "https://api.securitycenter.microsoft.com/",
            },
          ]}
        />

        <OrBorder />
      </>
    }
    fields={{
      baseUrl: {
        label: "Base URL",
        type: "url",
        defaultValue:
          instance?.api ?? "https://api.securitycenter.microsoft.com/",
        placeholder: "https://api.securitycenter.microsoft.com/",
        required: true,
        validator: z.string().url(),
      },
      tenantId: {
        label: "Tenant ID",
        defaultValue: instance?.username,
        placeholder: "Enter your Tenant ID",
        required: true,
        validator: z.string().min(1),
      },
      appId: {
        label: "App ID",
        placeholder: "Enter your App ID",
        required: true,
        validator: z.string().min(1),
      },
      appSecret: {
        label: "App Secret",
        type: "password",
        required: true,
        placeholder: `Enter your App Secret`,
        validator: z.string().min(1),
      },
    }}
    transformParams={({ baseUrl, tenantId, appId, appSecret }) => ({
      api: baseUrl,
      user: tenantId,
      secret: `${appId}/${appSecret}`,
    })}
  />
);
